import { Fragment, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import "./Breadcrumbs.css";

export type Crumb = {
  label: string;
  to?: string;
  title?: string;
  icon?: ReactNode;
  muted?: boolean;
};

type Props = {
  items: Crumb[];
  className?: string;
  variant?: "flat" | "pill";
};

export default function Breadcrumbs({ items, className, variant = "flat" }: Props) {
  const navigate = useNavigate();
  const lastIndex = items.length - 1;

  return (
    <nav
      className={`breadcrumbs ${variant} ${className ?? ""}`.trim()}
      aria-label="Breadcrumb"
    >
      <ol className="bc-list">
        {items.map((c, i) => {
          const isCurrent = !c.to || i === lastIndex;

          const segClasses = [
            "bc-seg",
            isCurrent ? "is-current" : "is-link",
            c.muted ? "is-muted" : "",
            variant === "pill" ? "is-pill" : "",
          ]
            .join(" ")
            .trim();

          const content = isCurrent ? (
            <span
              className={segClasses}
              aria-current="page"
              title={c.title ?? c.label}
            >
              {c.icon && <span className="bc-icon" aria-hidden>{c.icon}</span>}
              <span className="bc-text">{c.label}</span>
            </span>
          ) : (
            <button
              type="button"
              className={segClasses}
              onClick={() => c.to && navigate(c.to)}
              title={c.title ?? c.label}
            >
              {c.icon && <span className="bc-icon" aria-hidden>{c.icon}</span>}
              <span className="bc-text">{c.label}</span>
            </button>
          );

          return (
            <Fragment key={`${c.label}-${i}`}>
              <li className="bc-item">{content}</li>
              {i !== lastIndex && (
                <li className={`bc-sep ${variant === "pill" ? "sep-pill" : ""}`} aria-hidden="true">
                  <span>›</span>
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
